import { useState, useEffect } from 'react'
import { api } from '@/services/api-client'
import { useAvailableModels } from '@/hooks/queries'
import { useUIStore } from '@/stores'
import { Bot, Plus, Trash2, X, ChevronRight, ChevronUp, ChevronDown, FileText, Facebook, Loader2, Sparkles, Tag, BookOpen, ExternalLink, ArrowLeft, RefreshCw, ShoppingCart, Send, ListChecks, Image as ImageIcon, Calendar, Globe, CheckCircle, Clock, AlertCircle } from 'lucide-react'
import { getModelShortName } from '@/utils/helpers'
import { WysiwygEditor } from '@/components/ui/WysiwygEditor'

type Session = {
  id: string
  topic: string
  description: string
  keywords: string[]
  target_count: number
  generated_count: number
  status: string
  provider: string
  model: string
  created_at: string
  articles?: Article[]
}

type Article = {
  id: string
  title: string
  content: string
  bibliography: { source: string; url?: string; description?: string }[]
  article_type: 'blog' | 'facebook'
  word_count: number
  status: string
  sort_order: number
}

export function AgentView() {
  const { data: availableModels } = useAvailableModels()
  const navigateToMarketplace = useUIStore(s => s.navigateToMarketplace)
  const hasProviders = availableModels && availableModels.length > 0
  const [sessions, setSessions] = useState<Session[]>([])
  const [activeSession, setActiveSession] = useState<Session | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState<string | null>(null)
  const [openArticle, setOpenArticle] = useState<Article | null>(null)
  const [keywordInput, setKeywordInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set())
  const [bulkGenerating, setBulkGenerating] = useState(false)
  const [showAppendForm, setShowAppendForm] = useState(false)
  const [appendCount, setAppendCount] = useState('5')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [showQueue, setShowQueue] = useState(false)
  const [queueItems, setQueueItems] = useState<any[]>([])
  const [queueLoading, setQueueLoading] = useState(false)
  const [publishing, setPublishing] = useState<string | null>(null)
  const [imageSearch, setImageSearch] = useState<{ itemId: string; images: any[]; loading: boolean } | null>(null)
  const [blogAccounts, setBlogAccounts] = useState<any[]>([])
  const [editingContent, setEditingContent] = useState<string | null>(null)
  const [savingArticle, setSavingArticle] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [schedule, setSchedule] = useState({
    blog_account_id: '', frequency: 'weekly', time_mode: 'preset', time_hour: 'morning',
    time_range_start: '08:00', time_range_end: '18:00', fixed_hour: '10:00',
  })

  const [form, setForm] = useState({
    topic: '', description: '', keywords: [] as string[],
    target_count: '5', provider: '', model: '',
  })

  const selectedProviderModels = availableModels?.find(p => p.provider_type === form.provider)?.models || []

  useEffect(() => {
    if (availableModels && availableModels.length > 0 && !form.provider) {
      const first = availableModels[0]
      setForm(f => ({ ...f, provider: first.provider_type, model: first.models[0] || '' }))
    }
  }, [availableModels])

  const fetchSessions = async () => {
    setLoading(true)
    try { setSessions(await api.getAgentSessions()) } catch { /* */ } finally { setLoading(false) }
  }

  const fetchSession = async (id: string) => {
    setLoading(true)
    try {
      const s = await api.getAgentSession(id)
      setActiveSession(s)
    } catch { /* */ } finally { setLoading(false) }
  }

  useEffect(() => { fetchSessions(); fetchBlogAccounts() }, [])

  const handleAddKeyword = () => {
    const kw = keywordInput.trim()
    if (kw && !form.keywords.includes(kw)) {
      setForm(f => ({ ...f, keywords: [...f.keywords, kw] }))
      setKeywordInput('')
    }
  }

  const handleRemoveKeyword = (kw: string) => {
    setForm(f => ({ ...f, keywords: f.keywords.filter(k => k !== kw) }))
  }

  const handleCreate = async () => {
    if (!form.topic.trim() || !form.provider || !form.model) return
    setError(null)
    try {
      const session = await api.createAgentSession({
        topic: form.topic, description: form.description, keywords: form.keywords,
        target_count: parseInt(form.target_count) || 5,
        provider: form.provider, model: form.model,
      })
      setForm({ topic: '', description: '', keywords: [], target_count: '5', provider: '', model: '' })
      setShowCreate(false)
      setActiveSession(session)
      fetchSessions()
    } catch (err: any) { setError(err?.message || 'Nie udalo sie utworzyc sesji') }
  }

  // Poll a job until it completes, then refresh data
  const pollJob = async (jobId: string, sessionId?: string) => {
    const poll = setInterval(async () => {
      try {
        const job = await api.getJob(jobId)
        if (job.status === 'completed') {
          clearInterval(poll)
          setGenerating(null)
          setBulkGenerating(false)
          if (sessionId) { await fetchSession(sessionId); fetchSessions() }
        } else if (job.status === 'failed') {
          clearInterval(poll)
          setGenerating(null)
          setBulkGenerating(false)
          setError(job.error || 'Generowanie nie powiodlo sie')
          if (sessionId) await fetchSession(sessionId)
        }
      } catch { /* keep polling */ }
    }, 3000)
  }

  const handleGenerateTitles = async (sessionId: string) => {
    setGenerating('titles')
    setError(null)
    try {
      const { job_id } = await api.generateTitles(sessionId)
      pollJob(job_id, sessionId)
    } catch (err: any) { setError(err?.message || 'Nie udalo sie wygenerowac tytulow'); setGenerating(null) }
  }

  const handleAppendTitles = async (sessionId: string) => {
    const count = parseInt(appendCount) || 5
    setGenerating('titles')
    setError(null)
    try {
      const { job_id } = await api.appendTitles(sessionId, count)
      setShowAppendForm(false)
      setAppendCount('5')
      pollJob(job_id, sessionId)
    } catch (err: any) { setError(err?.message || 'Nie udalo sie dodac tytulow'); setGenerating(null) }
  }

  const handleGenerateArticle = async (articleId: string) => {
    setGenerating(articleId)
    setError(null)
    try {
      const { job_id } = await api.generateArticle(articleId)
      if (activeSession) pollJob(job_id, activeSession.id)
    } catch (err: any) { setError(err?.message || 'Nie udalo sie wygenerowac artykulu'); setGenerating(null) }
  }

  const toggleArticleSelect = (id: string) => {
    setSelectedArticles(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleBulkGenerate = async (articleIds: string[]) => {
    if (articleIds.length === 0) return
    setBulkGenerating(true)
    setGenerating('bulk')
    setError(null)
    try {
      const { job_ids } = await api.bulkGenerateArticles(articleIds)
      setSelectedArticles(new Set())
      // Poll last job to know when all are done
      if (job_ids.length > 0 && activeSession) {
        pollJob(job_ids[job_ids.length - 1], activeSession.id)
      }
    } catch (err: any) {
      setError(err?.message || 'Nie udalo sie uruchomic generowania')
      setBulkGenerating(false)
      setGenerating(null)
    }
  }

  const handleDeleteSession = async (id: string) => {
    try {
      await api.deleteAgentSession(id)
      if (activeSession?.id === id) setActiveSession(null)
      setConfirmDeleteId(null)
      fetchSessions()
    } catch { /* */ }
  }

  // ============ Queue functions ============
  const fetchQueue = async () => {
    setQueueLoading(true)
    try { setQueueItems(await api.getPublishQueue()) } catch { /* */ } finally { setQueueLoading(false) }
  }

  const fetchBlogAccounts = async () => {
    try {
      const accs = await api.getSocialAccounts()
      setBlogAccounts(accs.filter((a: any) => a.platform === 'blog_api'))
    } catch { /* */ }
  }

  const handleAddToQueue = async (article: Article) => {
    setError(null)
    try {
      const blogAcc = blogAccounts[0]
      await api.addToPublishQueue({
        article_id: article.id,
        title: article.title,
        content: article.content,
        target_site: blogAcc?.account_name || 'blog',
        publish_date: new Date().toISOString().substring(0, 16),
      })
    } catch (err: any) { setError(err?.message || 'Nie udalo sie dodac do kolejki') }
  }

  const handleBulkAddToQueue = async () => {
    if (!activeSession) return
    const articles = (activeSession.articles || []).filter(a => a.status === 'generated')
    const selected = selectedArticles.size > 0
      ? articles.filter(a => selectedArticles.has(a.id))
      : articles
    if (selected.length === 0) { setError('Brak wygenerowanych artykulow do dodania'); return }

    setError(null)
    try {
      await api.bulkAddToQueue({
        articles: selected.map(a => ({ article_id: a.id, title: a.title, content: a.content })),
        blog_account_id: schedule.blog_account_id,
        frequency: schedule.frequency,
        time_mode: schedule.time_mode,
        time_hour: schedule.time_mode === 'fixed' ? schedule.fixed_hour : schedule.time_mode === 'preset' ? schedule.time_hour : undefined,
        time_range_start: schedule.time_mode === 'range' ? schedule.time_range_start : undefined,
        time_range_end: schedule.time_mode === 'range' ? schedule.time_range_end : undefined,
      })
      setShowSchedule(false)
      setSelectedArticles(new Set())
      setShowQueue(true)
      fetchQueue()
    } catch (err: any) { setError(err?.message || 'Nie udalo sie dodac do kolejki') }
  }

  const handleMoveInQueue = async (id: string, direction: 'up' | 'down') => {
    const idx = queueItems.findIndex(q => q.id === id)
    if (idx < 0) return
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= queueItems.length) return
    const newItems = [...queueItems]
    ;[newItems[idx], newItems[swapIdx]] = [newItems[swapIdx], newItems[idx]]
    setQueueItems(newItems)
    try { await api.reorderQueue(newItems.map(q => q.id)) } catch { /* */ }
  }

  const handleRemoveFromQueue = async (id: string) => {
    try {
      await api.deleteQueueItem(id)
      setQueueItems(prev => prev.filter(q => q.id !== id))
    } catch { /* */ }
  }

  const handlePublish = async (id: string) => {
    setPublishing(id)
    setError(null)
    try {
      const result = await api.publishQueueItem(id)
      setQueueItems(prev => prev.map(q => q.id === id ? result : q))
    } catch (err: any) { setError(err?.message || 'Publikacja nie powiodla sie') } finally { setPublishing(null) }
  }

  const handleSuggestImage = async (itemId: string, title: string) => {
    setImageSearch({ itemId, images: [], loading: true })
    try {
      const result = await api.suggestImage(title)
      setImageSearch({ itemId, images: result.images || [], loading: false })
    } catch { setImageSearch({ itemId, images: [], loading: false }) }
  }

  const handleSelectImage = async (itemId: string, image: any) => {
    try {
      const updated = await api.updateQueueItem(itemId, { image_url: image.download_url, image_source: `${image.source}: ${image.author}` })
      setQueueItems(prev => prev.map(q => q.id === itemId ? updated : q))
      setImageSearch(null)
    } catch { /* */ }
  }

  const handleUpdateQueueDate = async (itemId: string, date: string) => {
    try {
      const updated = await api.updateQueueItem(itemId, { publish_date: date })
      setQueueItems(prev => prev.map(q => q.id === itemId ? updated : q))
    } catch { /* */ }
  }

  const handleUpdateQueueSite = async (itemId: string, site: string) => {
    try {
      const updated = await api.updateQueueItem(itemId, { target_site: site })
      setQueueItems(prev => prev.map(q => q.id === itemId ? updated : q))
    } catch { /* */ }
  }

  // ============ Queue view ============
  if (showQueue) {
    if (queueItems.length === 0 && !queueLoading) fetchQueue()
    return (
      <div className="agent-view">
        <div className="queue-header">
          <button className="agent-back-btn" onClick={() => setShowQueue(false)}>
            <ArrowLeft size={18} /> Agent AI
          </button>
          <div className="queue-title-row">
            <ListChecks size={24} className="agent-icon" />
            <div>
              <h1>Kolejka publikacji</h1>
              <p className="agent-desc">Artykuly zaakceptowane do publikacji na stronach</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="agent-error">
            <X size={14} onClick={() => setError(null)} style={{ cursor: 'pointer', flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        {queueLoading && <p className="agent-loading"><Loader2 size={18} className="spin" /> Ladowanie kolejki...</p>}

        {!queueLoading && queueItems.length === 0 && (
          <div className="agent-empty">
            <ListChecks size={48} />
            <h3>Kolejka pusta</h3>
            <p>Wygeneruj artykuly i dodaj je do kolejki przyciskiem "Dodaj do kolejki".</p>
          </div>
        )}

        <div className="queue-list">
          {queueItems.map(item => (
            <div key={item.id} className={`queue-card queue-card--${item.status}`}>
              <div className="queue-card-top">
                <div className="queue-card-info">
                  <h3 className="queue-card-title">{item.title}</h3>
                  <div className="queue-card-meta">
                    <span className={`queue-status queue-status--${item.status}`}>
                      {item.status === 'published' ? <><CheckCircle size={12} /> Opublikowany</> : <><Clock size={12} /> W kolejce</>}
                    </span>
                    <span className="queue-site"><Globe size={12} /> {item.target_site}</span>
                  </div>
                </div>
                {item.image_url && (
                  <img src={item.image_url} alt="" className="queue-card-thumb" />
                )}
              </div>

              <div className="queue-card-preview" dangerouslySetInnerHTML={{ __html: renderMarkdown((item.content || '').substring(0, 300) + '...') }} />

              {item.status !== 'published' && (
                <div className="queue-card-controls">
                  <label className="queue-control">
                    <Calendar size={14} />
                    <input
                      type="datetime-local"
                      value={(item.publish_date || '').substring(0, 16)}
                      onChange={e => handleUpdateQueueDate(item.id, e.target.value)}
                    />
                  </label>
                  <label className="queue-control">
                    <Globe size={14} />
                    <select value={item.target_site} onChange={e => handleUpdateQueueSite(item.id, e.target.value)}>
                      <option value="substraty.pl">Substraty.pl</option>
                    </select>
                  </label>
                </div>
              )}

              <div className="queue-card-actions">
                {item.status === 'queued' && (
                  <>
                    <div className="queue-reorder">
                      <button className="queue-reorder-btn" onClick={() => handleMoveInQueue(item.id, 'up')} title="W gore"><ChevronUp size={14} /></button>
                      <button className="queue-reorder-btn" onClick={() => handleMoveInQueue(item.id, 'down')} title="W dol"><ChevronDown size={14} /></button>
                    </div>
                    <button className="queue-btn queue-btn--image" onClick={() => handleSuggestImage(item.id, item.title)}>
                      <ImageIcon size={14} /> {item.image_url ? 'Zmien obraz' : 'Zaproponuj obraz'}
                    </button>
                    <button className="queue-btn queue-btn--publish" onClick={() => handlePublish(item.id)} disabled={publishing === item.id}>
                      {publishing === item.id ? <><Loader2 size={14} className="spin" /> Publikowanie...</> : <><Send size={14} /> Publikuj teraz</>}
                    </button>
                    <button className="queue-btn queue-btn--delete" onClick={() => handleRemoveFromQueue(item.id)}>
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
                {item.status === 'publishing' && (
                  <span className="queue-status queue-status--publishing"><Loader2 size={12} className="spin" /> Publikowanie...</span>
                )}
                {item.status === 'published' && (
                  <span className="queue-published-info"><CheckCircle size={12} /> Opublikowano{item.published_sid ? ` (SID: ${item.published_sid})` : ''}</span>
                )}
                {item.status === 'failed' && (
                  <>
                    <span className="queue-status queue-status--failed"><AlertCircle size={12} /> Blad publikacji</span>
                    <button className="queue-btn queue-btn--publish" onClick={() => handlePublish(item.id)}><RefreshCw size={14} /> Ponow</button>
                    <button className="queue-btn queue-btn--delete" onClick={() => handleRemoveFromQueue(item.id)}><Trash2 size={14} /></button>
                  </>
                )}
              </div>

              {/* Image search results */}
              {imageSearch && imageSearch.itemId === item.id && (
                <div className="queue-image-search">
                  {imageSearch.loading ? (
                    <p className="agent-loading"><Loader2 size={16} className="spin" /> Szukam obrazow...</p>
                  ) : imageSearch.images.length === 0 ? (
                    <p className="queue-no-images">Nie znaleziono obrazow. Sprobuj inny tytul.</p>
                  ) : (
                    <div className="queue-image-grid">
                      {imageSearch.images.map(img => (
                        <div key={img.id} className="queue-image-option" onClick={() => handleSelectImage(item.id, img)}>
                          <img src={img.thumb} alt={img.alt} />
                          <span className="queue-image-credit">{img.source}: {img.author}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button className="agent-back-btn" onClick={() => setImageSearch(null)}><X size={14} /> Zamknij</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Article popup/modal
  if (openArticle) {
    return (
      <div className="agent-view">
        <div className="agent-article-modal">
          <div className="agent-article-modal-header">
            <button className="agent-back-btn" onClick={() => setOpenArticle(null)}>
              <ArrowLeft size={18} /> Wstecz
            </button>
            <span className={`agent-type-badge agent-type-${openArticle.article_type}`}>
              {openArticle.article_type === 'blog' ? <><FileText size={13} /> Blog</> : <><Facebook size={13} /> Facebook</>}
            </span>
            {openArticle.word_count > 0 && <span className="agent-word-count">{openArticle.word_count} slow</span>}
          </div>
          <h1 className="agent-article-title">{openArticle.title}</h1>
          {openArticle.status === 'generated' ? (
            <>
              <div className="agent-article-actions-bar">
                {editingContent !== null ? (
                  <>
                    <button className="agent-generate-btn agent-generate-btn--all" onClick={async () => {
                      setSavingArticle(true)
                      try {
                        const updated = await api.updateAgentArticle(openArticle.id, { content: editingContent })
                        setOpenArticle({ ...openArticle, content: updated.content || editingContent, word_count: updated.word_count || 0 })
                        setEditingContent(null)
                        if (activeSession) fetchSession(activeSession.id)
                      } catch { /* */ } finally { setSavingArticle(false) }
                    }} disabled={savingArticle}>
                      {savingArticle ? <><Loader2 size={14} className="spin" /> Zapisywanie...</> : <><CheckCircle size={14} /> Zapisz</>}
                    </button>
                    <button className="agent-back-btn" onClick={() => setEditingContent(null)}><X size={14} /> Anuluj</button>
                  </>
                ) : (
                  <button className="agent-generate-btn agent-generate-btn--append" onClick={() => setEditingContent(openArticle.content)}>
                    <FileText size={14} /> Edytuj tresc
                  </button>
                )}
              </div>

              {editingContent !== null ? (
                <WysiwygEditor value={editingContent} onChange={setEditingContent} placeholder="Edytuj tresc artykulu..." />
              ) : (
                <div className="agent-article-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(openArticle.content) }} />
              )}
              {editingContent === null && openArticle.bibliography && openArticle.bibliography.length > 0 && (
                <div className="agent-bibliography">
                  <h3><BookOpen size={16} /> Bibliografia</h3>
                  <ul>
                    {openArticle.bibliography.map((b, i) => (
                      <li key={i}>
                        <strong>{b.source}</strong>
                        {b.description && <span> - {b.description}</span>}
                        {b.url && <a href={b.url} target="_blank" rel="noopener noreferrer"><ExternalLink size={12} /></a>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="agent-article-empty">
              <p>Artykul jeszcze nie zostal wygenerowany.</p>
              <button className="agent-generate-btn" onClick={() => handleGenerateArticle(openArticle.id)} disabled={generating === openArticle.id}>
                {generating === openArticle.id ? <><Loader2 size={14} className="spin" /> Generowanie...</> : <><Sparkles size={14} /> Generuj tresc</>}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Session detail view
  if (activeSession) {
    const articles = activeSession.articles || []
    const draftArticles = articles.filter(a => a.status !== 'generated')
    return (
      <div className="agent-view">
        <div className="agent-session-detail">
          <div className="agent-detail-header">
            <button className="agent-back-btn" onClick={() => { setActiveSession(null); fetchSessions() }}>
              <ArrowLeft size={18} /> Sesje
            </button>
            <div className="agent-detail-info">
              <h2>{activeSession.topic}</h2>
              {activeSession.description && <p className="agent-detail-desc">{activeSession.description}</p>}
              <div className="agent-detail-meta">
                <span className="agent-meta-item"><Tag size={12} /> {activeSession.keywords.join(', ')}</span>
                <span className="agent-meta-item">{getModelShortName(activeSession.model)}</span>
                <span className="agent-meta-item">Cel: {activeSession.target_count} artykulow</span>
              </div>
            </div>
          </div>

          <div className="agent-actions-bar">
            <button className="agent-generate-btn" onClick={() => handleGenerateTitles(activeSession.id)} disabled={generating === 'titles' || bulkGenerating}>
              {generating === 'titles' ? <><Loader2 size={14} className="spin" /> Generowanie tytulów...</> : <><RefreshCw size={14} /> {articles.length > 0 ? 'Regeneruj tytuly' : 'Generuj tytuly'}</>}
            </button>
            {articles.length > 0 && (
              <button className="agent-generate-btn agent-generate-btn--append" onClick={() => setShowAppendForm(!showAppendForm)} disabled={generating === 'titles'}>
                <Plus size={14} /> Dodaj paczke
              </button>
            )}
            {articles.filter(a => a.status === 'generated').length > 0 && blogAccounts.length > 0 && (
              <button className="agent-generate-btn queue-btn--publish" onClick={() => setShowSchedule(!showSchedule)}>
                <Send size={14} /> Dodaj do bloga
              </button>
            )}
            {draftArticles.length > 0 && (
              <>
                {selectedArticles.size > 0 ? (
                  <button className="agent-generate-btn agent-generate-btn--selected" onClick={() => handleBulkGenerate(Array.from(selectedArticles))} disabled={bulkGenerating}>
                    {bulkGenerating ? <><Loader2 size={14} className="spin" /> Generowanie...</> : <><Sparkles size={14} /> Generuj zaznaczone ({selectedArticles.size})</>}
                  </button>
                ) : (
                  <button className="agent-generate-btn agent-generate-btn--all" onClick={() => handleBulkGenerate(draftArticles.map(a => a.id))} disabled={bulkGenerating}>
                    {bulkGenerating ? <><Loader2 size={14} className="spin" /> Generowanie...</> : <><Sparkles size={14} /> Generuj wszystkie ({draftArticles.length})</>}
                  </button>
                )}
              </>
            )}
            {articles.length > 0 && (
              <span className="agent-articles-count">{articles.filter(a => a.status === 'generated').length}/{articles.length} wygenerowanych</span>
            )}
          </div>

          {showAppendForm && (
            <div className="agent-append-form">
              <span>Ile nowych tytulow wygenerowac?</span>
              <input type="number" min="1" max="50" value={appendCount} onChange={e => setAppendCount(e.target.value)} className="agent-append-input" />
              <button className="agent-generate-btn agent-generate-btn--append" onClick={() => handleAppendTitles(activeSession.id)} disabled={generating === 'titles'}>
                {generating === 'titles' ? <><Loader2 size={14} className="spin" /> Generowanie...</> : <><Sparkles size={14} /> Generuj</>}
              </button>
              <button className="agent-back-btn" onClick={() => setShowAppendForm(false)}><X size={14} /></button>
            </div>
          )}

          {showSchedule && (
            <div className="queue-schedule-form">
              <h3><Send size={16} /> Harmonogram publikacji na blogu</h3>
              <p className="agent-desc">
                {selectedArticles.size > 0
                  ? `Dodaj ${selectedArticles.size} zaznaczonych artykulow do kolejki`
                  : `Dodaj ${articles.filter(a => a.status === 'generated').length} wygenerowanych artykulow do kolejki`}
              </p>
              <div className="queue-schedule-grid">
                {blogAccounts.length > 0 && (
                  <label className="agent-field">
                    <span><Globe size={14} /> Blog docelowy</span>
                    <select value={schedule.blog_account_id} onChange={e => setSchedule(s => ({ ...s, blog_account_id: e.target.value }))}>
                      <option value="">-- Wybierz blog --</option>
                      {blogAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                    </select>
                  </label>
                )}
                <label className="agent-field">
                  <span><Calendar size={14} /> Czestotliwosc</span>
                  <select value={schedule.frequency} onChange={e => setSchedule(s => ({ ...s, frequency: e.target.value }))}>
                    <option value="daily">Codziennie</option>
                    <option value="weekly">Co tydzien</option>
                    <option value="biweekly">Co 2 tygodnie</option>
                    <option value="monthly">Co miesiac</option>
                  </select>
                </label>
                <label className="agent-field">
                  <span><Clock size={14} /> Pora publikacji</span>
                  <select value={schedule.time_mode} onChange={e => setSchedule(s => ({ ...s, time_mode: e.target.value }))}>
                    <option value="preset">Pora dnia</option>
                    <option value="fixed">O stalej godzinie</option>
                    <option value="range">Zakres godzin</option>
                  </select>
                </label>
                {schedule.time_mode === 'preset' && (
                  <label className="agent-field">
                    <span>Pora</span>
                    <select value={schedule.time_hour} onChange={e => setSchedule(s => ({ ...s, time_hour: e.target.value }))}>
                      <option value="morning">Rano (7:00-10:00)</option>
                      <option value="noon">Poludnie (11:00-14:00)</option>
                      <option value="evening">Wieczor (17:00-20:00)</option>
                      <option value="night">Noc (21:00-24:00)</option>
                    </select>
                  </label>
                )}
                {schedule.time_mode === 'fixed' && (
                  <label className="agent-field">
                    <span>Godzina</span>
                    <input type="time" value={schedule.fixed_hour} onChange={e => setSchedule(s => ({ ...s, fixed_hour: e.target.value }))} />
                  </label>
                )}
                {schedule.time_mode === 'range' && (
                  <>
                    <label className="agent-field">
                      <span>Od</span>
                      <input type="time" value={schedule.time_range_start} onChange={e => setSchedule(s => ({ ...s, time_range_start: e.target.value }))} />
                    </label>
                    <label className="agent-field">
                      <span>Do</span>
                      <input type="time" value={schedule.time_range_end} onChange={e => setSchedule(s => ({ ...s, time_range_end: e.target.value }))} />
                    </label>
                  </>
                )}
              </div>
              <div className="queue-schedule-actions">
                <button className="queue-btn queue-btn--publish" onClick={handleBulkAddToQueue} disabled={!schedule.blog_account_id}>
                  <Send size={14} /> Dodaj do kolejki
                </button>
                <button className="agent-back-btn" onClick={() => setShowSchedule(false)}><X size={14} /> Anuluj</button>
              </div>
            </div>
          )}

          {error && (
            <div className="agent-error">
              <X size={14} onClick={() => setError(null)} style={{ cursor: 'pointer', flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          {draftArticles.length > 0 && articles.length > 0 && (
            <div className="agent-select-all">
              <label>
                <input
                  type="checkbox"
                  checked={draftArticles.length > 0 && draftArticles.every(a => selectedArticles.has(a.id))}
                  onChange={() => {
                    const allSelected = draftArticles.every(a => selectedArticles.has(a.id))
                    setSelectedArticles(allSelected ? new Set() : new Set(draftArticles.map(a => a.id)))
                  }}
                />
                Zaznacz wszystkie szkice ({draftArticles.length})
              </label>
            </div>
          )}

          {articles.length === 0 ? (
            <div className="agent-empty-articles">
              <Sparkles size={32} />
              <p>Kliknij "Generuj tytuly" aby Agent AI zaproponowal tytuly artykulow</p>
            </div>
          ) : (
            <div className="agent-articles-list">
              {articles.map((a) => (
                <div key={a.id} className={`agent-article-card agent-article-card--${a.status}${selectedArticles.has(a.id) ? ' agent-article-card--selected' : ''}`}>
                  <div className="agent-article-card-left">
                    {a.status !== 'generated' && (
                      <input
                        type="checkbox"
                        className="agent-article-checkbox"
                        checked={selectedArticles.has(a.id)}
                        onChange={() => toggleArticleSelect(a.id)}
                      />
                    )}
                    <span className={`agent-type-badge agent-type-${a.article_type}`}>
                      {a.article_type === 'blog' ? <FileText size={13} /> : <Facebook size={13} />}
                    </span>
                    <div className="agent-article-card-info">
                      <button className="agent-article-link" onClick={() => setOpenArticle(a)}>
                        {a.title}
                        <ChevronRight size={14} />
                      </button>
                      <div className="agent-article-card-meta">
                        <span className={`agent-status-badge agent-status-${a.status}`}>
                          {a.status === 'generated' ? 'Gotowy' : 'Szkic'}
                        </span>
                        {a.word_count > 0 && <span>{a.word_count} slow</span>}
                      </div>
                    </div>
                  </div>
                  <div className="agent-article-card-actions">
                    {a.status !== 'generated' ? (
                      <button className="agent-generate-sm-btn" onClick={() => handleGenerateArticle(a.id)} disabled={generating === a.id}>
                        {generating === a.id ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
                      </button>
                    ) : (
                      <>
                        <button className="agent-view-btn" onClick={() => setOpenArticle(a)}>
                          Czytaj
                        </button>
                        <button className="queue-add-btn" onClick={() => handleAddToQueue(a)} title="Dodaj do kolejki publikacji">
                          <Send size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Sessions list view
  return (
    <div className="agent-view">
      <div className="agent-header">
        <div className="agent-title-row">
          <Bot size={24} className="agent-icon" />
          <div>
            <h1>Agent AI</h1>
            <p className="agent-desc">Automatyczne tworzenie artykulow blogowych i postow na Facebooka</p>
          </div>
        </div>
        <div className="agent-header-actions">
          <button className="agent-queue-btn" onClick={() => { setShowQueue(true); fetchQueue() }}>
            <ListChecks size={16} /> Kolejka{queueItems.length > 0 ? ` (${queueItems.filter(q => q.status !== 'published').length})` : ''}
          </button>
          <button className="agent-create-btn" onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? <><X size={16} /> Anuluj</> : <><Plus size={16} /> Nowa sesja</>}
          </button>
        </div>
      </div>

      {error && (
        <div className="agent-error">
          <X size={14} onClick={() => setError(null)} style={{ cursor: 'pointer', flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}

      {showCreate && (
        <div className="agent-create-form">
          <div className="agent-form-grid">
            <label className="agent-field agent-field--wide">
              <span>Temat</span>
              <input type="text" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} placeholder="np. Najnowsze technologie biogazowe" />
            </label>
            <label className="agent-field agent-field--wide">
              <span>Opis (opcjonalny)</span>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Dodatkowy kontekst dla agenta..." rows={2} />
            </label>
            <div className="agent-field agent-field--wide">
              <span><Tag size={14} /> Slowa kluczowe</span>
              <div className="agent-keywords-input">
                <input type="text" value={keywordInput} onChange={e => setKeywordInput(e.target.value)} placeholder="Dodaj slowo kluczowe..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddKeyword() } }} />
                <button type="button" onClick={handleAddKeyword}><Plus size={14} /></button>
              </div>
              {form.keywords.length > 0 && (
                <div className="agent-keywords-list">
                  {form.keywords.map(kw => (
                    <span key={kw} className="agent-keyword-tag">
                      {kw}
                      <button onClick={() => handleRemoveKeyword(kw)}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <label className="agent-field">
              <span>Liczba artykulow</span>
              <input type="number" min="1" max="50" value={form.target_count} onChange={e => setForm(f => ({ ...f, target_count: e.target.value }))} />
            </label>
            {hasProviders ? (
              <>
                <label className="agent-field">
                  <span>Provider AI</span>
                  <select value={form.provider} onChange={e => {
                    const prov = e.target.value
                    const models = availableModels?.find(p => p.provider_type === prov)?.models || []
                    setForm(f => ({ ...f, provider: prov, model: models[0] || '' }))
                  }}>
                    <option value="">-- Wybierz provider --</option>
                    {availableModels?.map(p => (
                      <option key={p.provider_type} value={p.provider_type}>{p.display_name}</option>
                    ))}
                  </select>
                </label>
                <label className="agent-field">
                  <span>Model</span>
                  <select value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} disabled={!form.provider}>
                    <option value="">-- Wybierz model --</option>
                    {selectedProviderModels.map(m => (
                      <option key={m} value={m}>{getModelShortName(m)}</option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <div className="agent-field agent-field--wide agent-no-provider">
                <ShoppingCart size={18} />
                <div>
                  <p>Nie masz skonfigurowanego klucza API.</p>
                  <p>Dodaj wlasny klucz w ustawieniach lub wypozycz na gieldzie.</p>
                </div>
                <button className="agent-marketplace-btn" onClick={navigateToMarketplace}>
                  <ShoppingCart size={14} /> Przejdz do Gieldy API
                </button>
              </div>
            )}
          </div>
          <button className="agent-submit-btn" onClick={handleCreate} disabled={!form.topic.trim() || !form.provider || !form.model}>
            <Sparkles size={14} /> Utwórz sesje agenta
          </button>
        </div>
      )}

      <div className="agent-sessions-list">
        {loading && <p className="agent-loading"><Loader2 size={18} className="spin" /> Ladowanie...</p>}
        {!loading && sessions.length === 0 && !showCreate && (
          <div className="agent-empty">
            <Bot size={48} />
            <h3>Brak sesji agenta</h3>
            <p>Utwórz nowa sesje, aby Agent AI zaczal generowac artykuly i posty dla Ciebie.</p>
            <button className="agent-create-btn" onClick={() => setShowCreate(true)}>
              <Plus size={16} /> Nowa sesja
            </button>
          </div>
        )}
        {sessions.map(s => (
          <div key={s.id} className="agent-session-card" onClick={() => fetchSession(s.id)}>
            <div className="agent-session-card-left">
              <Bot size={20} className="agent-session-icon" />
              <div>
                <h3 className="agent-session-topic">{s.topic}</h3>
                <div className="agent-session-meta">
                  <span>{s.keywords.slice(0, 4).join(', ')}{s.keywords.length > 4 ? '...' : ''}</span>
                  <span>{getModelShortName(s.model)}</span>
                  <span>{s.generated_count}/{s.target_count} artykulow</span>
                </div>
              </div>
            </div>
            <div className="agent-session-card-right">
              <span className="agent-session-date">{new Date(s.created_at).toLocaleDateString()}</span>
              <button className="agent-delete-btn" onClick={e => { e.stopPropagation(); setConfirmDeleteId(s.id) }} title="Usun">
                <Trash2 size={14} />
              </button>
              <ChevronRight size={16} />
            </div>
          </div>
        ))}
      </div>

      {confirmDeleteId && (
        <div className="agent-confirm-overlay" onClick={() => setConfirmDeleteId(null)}>
          <div className="agent-confirm-popup" onClick={e => e.stopPropagation()}>
            <h3><Trash2 size={18} /> Usunac sesje?</h3>
            <p>Wszystkie artykuly w tej sesji zostana trwale usuniete. Tej operacji nie mozna cofnac.</p>
            <div className="agent-confirm-actions">
              <button className="agent-confirm-cancel" onClick={() => setConfirmDeleteId(null)}>Anuluj</button>
              <button className="agent-confirm-delete" onClick={() => handleDeleteSession(confirmDeleteId)}>
                <Trash2 size={14} /> Usun
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Simple markdown to HTML converter
function renderMarkdown(md: string): string {
  return md
    .replace(/### (.*?)$/gm, '<h3>$1</h3>')
    .replace(/## (.*?)$/gm, '<h2>$1</h2>')
    .replace(/# (.*?)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>')
}
